import type { MeResponse } from "@station/contracts";

/**
 * Filo PORTALI erişim kuralları (Prompt 17 web, ADR 0017 §17) — saf ve birim testlidir.
 *
 * Portal kullanıcısı = etkin kiracıdaki atamalarının TAMAMI `COUNTERPARTY` kapsamlı (yalnızca `FLEET_PORTAL_USER`
 * rolü bu kapsamda atanabilir) olan kullanıcı. Böyle bir kullanıcı girişte `/portal`'a yönlenir ve personel ekranlarından
 * geri gönderilir (kendi hesap güvenliği ve profil sayfaları hariç). Personel (en az bir personel ataması olan) portala
 * girerse açık bir ileti görür; cari kimliği ZATEN sunucuda atamadan türetilir (istemci karar vermez).
 */
type ActiveTenant = NonNullable<MeResponse["activeTenant"]>;

export interface PortalAccess {
  /** Portal erişim izni + en az bir cari kapsamlı atama. */
  hasPortalGrant: boolean;
  /** Atamaların TAMAMI cari kapsamlı: personel ekranları bu kullanıcı için anlamsızdır. */
  portalOnly: boolean;
  /** Atamadan gelen filo carileri (sıra atama sırası). */
  counterpartyIds: string[];
}

export function portalAccess(
  tenant: Pick<ActiveTenant, "assignments" | "permissions"> | null | undefined,
): PortalAccess {
  if (!tenant) return { hasPortalGrant: false, portalOnly: false, counterpartyIds: [] };
  const counterpartyIds = [
    ...new Set(
      tenant.assignments
        .filter((assignment) => assignment.scopeType === "COUNTERPARTY" && assignment.scopeId !== null)
        .map((assignment) => assignment.scopeId!),
    ),
  ];
  const hasPortalGrant = counterpartyIds.length > 0 && tenant.permissions.includes("fleet.portal.access");
  const portalOnly =
    tenant.assignments.length > 0 &&
    tenant.assignments.every((assignment) => assignment.scopeType === "COUNTERPARTY");
  return { hasPortalGrant, portalOnly, counterpartyIds };
}

/** Portal kullanıcısının personel kabuğunda da açabileceği KENDİ hesap sayfaları. */
export const PORTAL_ALLOWED_STAFF_PATHS = ["/ayarlar/guvenlik", "/ayarlar/profil"] as const;

/**
 * Personel kabuğundaki (`/_app`) bir adres için yönlendirme kararı: yalnızca portal kullanıcısı ve izinli hesap
 * sayfalarının dışında → `/portal`. Kurulum sihirbazı, ana sayfa, menü ekranları portal kullanıcısına kapalıdır.
 */
export function staffRouteRedirect(pathname: string, access: PortalAccess): "/portal" | null {
  if (!access.portalOnly) return null;
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  return (PORTAL_ALLOWED_STAFF_PATHS as readonly string[]).includes(path) ? null : "/portal";
}

/**
 * Birden çok filo carisine erişimi olan kullanıcıda seçili cari: kayıtlı seçim hâlâ listedeyse o, değilse ilk cari.
 * Liste boşsa `null` (portal boş durum çizer).
 */
export function selectPortalAccount(accountIds: readonly string[], stored: string | null): string | null {
  if (accountIds.length === 0) return null;
  return stored !== null && accountIds.includes(stored) ? stored : accountIds[0]!;
}

/**
 * Kuraldaki izinli ürün / istasyon adlarının kısa metni: en çok `limit` ad, fazlası "+N" (sıra sunucunun sırası —
 * kimliklerle aynı). Boş liste "hiçbiri" demektir; çağıran `null`'da ayrı metin gösterir.
 */
export function namesText(refs: ReadonlyArray<{ name: string }>, limit = 3): string | null {
  if (refs.length === 0) return null;
  const shown = refs.slice(0, limit).map((ref) => ref.name);
  return refs.length > limit ? `${shown.join(", ")} +${refs.length - limit}` : shown.join(", ");
}
